import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import { useState, useMemo, useEffect, memo, type ChangeEventHandler } from 'react';
import { Icon, SxProps, Theme, Tooltip } from '@mui/material';
import { uploadAudio } from '@modules/firebase/storage';
import { toast } from 'react-toastify';
import { useInterviewOptions } from '@providers/InterviewsProvider';

export interface UploadFileInfo {
    url: string;
    name: string;
    type: string;
}

interface Props {
    onUploadFinish: (fileInfo: UploadFileInfo) => void;
    path: string;
    fileUploadedPath: false | string;
    style?: SxProps<Theme> | undefined;
    generalAudio?: boolean;
}

const AudioUploadIconButton = ({
    onUploadFinish,
    path,
    fileUploadedPath,
    style,
    generalAudio,
}: Props) => {
    const acceptedFormats = '*/audio/.mp3';
    const [selectedFile, setSelectedFile] = useState<File>();
    const { selectedInterview } = useInterviewOptions();
    const groupId = selectedInterview?.groupId;
    const interviewId = selectedInterview?.id;

    const fullPath = useMemo(
        () => `${groupId}/${interviewId}/${path}`,
        [path, groupId, interviewId],
    );

    useEffect(() => {
        let mounted = true;
        const processUpload = async () => {
            if (selectedFile) {
                toast.promise(
                    uploadAudio(selectedFile, fullPath, () => {}, onUploadFinish),
                    {
                        pending: 'Audio file uploading...',
                        success: 'Audio file uploaded 👌',
                        error: 'Error uploading audio file 🤯',
                    },
                    { autoClose: 2000 },
                );
            }
        };
        if (mounted) {
            processUpload();
        }
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFile]);

    const handleFileSelect: ChangeEventHandler<HTMLInputElement> = (event) => {
        setSelectedFile(event?.target?.files?.[0]);
    };

    const icon = fileUploadedPath ? (
        <CloudDoneOutlinedIcon color="info" />
    ) : (
        <AudioFileOutlinedIcon color="info" />
    );

    const toolTip = fileUploadedPath
        ? 'You have already uploaded a file. Upload a different one?'
        : 'Upload audio file for this question.';

    return (
        <Tooltip sx={style} title={toolTip}>
            <Icon
                sx={style}
                component="label"
                onClick={() => {
                    if (!generalAudio) document.getElementById(path)?.click();
                }}
            >
                <input
                    id={path}
                    style={{ display: 'none' }}
                    type="file"
                    accept={acceptedFormats}
                    hidden
                    onChange={handleFileSelect}
                    onClick={(e) => e.stopPropagation()}
                />
                {icon}
            </Icon>
        </Tooltip>
    );
};

export default memo(AudioUploadIconButton);
